/**
 * Что сейчас главное — и что стоит следом.
 *
 * Прежний `diagnose` называл одну задачу на **месячном** горизонте: «свести
 * месяц». Человеку в дефиците это отвечает не на тот вопрос — его задача
 * дожить до пятницы, а не свести октябрь. Поэтому здесь появляются задачи
 * ближнего горизонта, и они стоят впереди месячных.
 *
 * Вторая перемена — очередь. Одна главная задача не означает одну известную:
 * через два дня ипотека, через пять грейс по кредитке, месяц не сходится и
 * подушки нет — всё это может быть верно одновременно. Показать только первое
 * значит заставить человека держать остальное в голове, а он открыл
 * приложение ровно чтобы этого не делать.
 */
import { ZERO, add, clampPos, formatMoney, sub, type Money } from './money.js';
import { addDays, isAfter, type PlainDate } from './date.js';
import { isKnown, type Amount } from './amount.js';
import { paymentInstances, type PaymentInstance } from './instances.js';
import { focusOptions, type Focus, type FocusInput, type FocusKind } from './focus.js';
import { computeCycleOutlook } from './verdict.js';
import { computeLiquidity } from './runway.js';
import { annualCost, savedPerYear } from './loans.js';
import type { AvailableResult } from './available.js';
import type { CreditState } from './credit.js';
import type { Account, Goal, Loan, Reserve } from './types.js';

export type PriorityKind =
  // Ближний горизонт: новые задачи, которых у `diagnose` не было.
  | 'survive_to_income' | 'cover_obligations' | 'avoid_interest_or_overdue'
  // Месячные и будущие — прежние значения `FocusKind`, строки не меняются:
  // они уже сохранены в `meta.focusOverride` у тех, кто выбирал задачу сам.
  | FocusKind;

/** К какому требованию относится задача и в каком оно состоянии. */
export interface PriorityTarget {
  kind: 'obligation' | 'credit' | 'loan' | 'goal';
  id: string;
  dueDate?: PlainDate;
  /** Обеспечено деньгами. */
  secured: Money;
  /** Не обеспечено. */
  missing: Money;
  /**
   * Исполнено ли фактически — отдельно от обеспечения.
   *
   * Без этого поля «деньги готовы, платёж не сделан» неотличимо от
   * «оплачено», и именно на этом смешении строился дефект FIX-2.
   */
  execution: 'not_due' | 'due' | 'overdue' | 'paid';
}

export interface Priority {
  kind: PriorityKind;
  horizon: 'cycle' | 'month' | 'future';
  title: string;
  /** Причина в деньгах, а не в процентах. Показывается дословно. */
  why: string;
  amount: Money;
  action: string;
  target?: PriorityTarget;
  applicable: boolean;
  chosen: boolean;
}

export interface PriorityStack {
  /** Что делать сейчас. Ровно одна задача. */
  primary: Priority;
  /** Что наступает раньше, чем закроется главная. Только задачи с датой. */
  next: Priority[];
  /** Что вернётся в фокус потом. Без дат. */
  later: Priority[];
  /** Всё по порядку, включая неактуальное, — для экрана выбора. */
  all: Priority[];
}

export interface PriorityInput extends FocusInput {
  available: AvailableResult;
  accounts: readonly Account[];
  reserves?: readonly Reserve[];
  needBaselineAmount: Amount;
  wantBaselineAmount: Amount;
  /** Докуда смотрим на очередь платежей. По умолчанию 45 дней. */
  queueDays?: number;
}

/**
 * Дата словами: «9 октября».
 *
 * «2026-10-09» человек не читает: чтобы понять, далеко ли это, ему приходится
 * считать в уме. В задаче, от которой зависят деньги, этого требовать нельзя.
 */
const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
const humanDate = (d: PlainDate): string => {
  const [, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS[Number(m) - 1]}`;
};

const CYCLE_ORDER: PriorityKind[] = [
  'survive_to_income', 'cover_obligations', 'avoid_interest_or_overdue',
];

/** Задачи без даты: они никогда не попадают в очередь «дальше». */
const UNDATED: PriorityKind[] = ['income_gap', 'expensive_debt', 'unknown_need', 'no_buffer', 'goals', 'nothing_yet'];

function executionOf(inst: PaymentInstance, today: PlainDate): PriorityTarget['execution'] {
  if (inst.settled) return 'paid';
  if (isAfter(inst.scheduledDate, today)) return 'not_due';
  return inst.scheduledDate === today ? 'due' : 'overdue';
}

/**
 * Ближний горизонт: дожить до прихода, обеспечить платежи, не попасть на
 * проценты. Считается из календаря наступлений, а не из `upcoming`: последний
 * обрезан горизонтом дохода и дальних платежей не содержит.
 */
function cyclePriorities(input: PriorityInput): Priority[] {
  const today = input.today;
  const queueTo = addDays(today, input.queueDays ?? 45);
  const instances = paymentInstances({
    obligations: input.obligations,
    ...(input.reserves ? { reserves: input.reserves } : {}),
    today,
    from: today,
    to: queueTo,
  });

  const out: Priority[] = [];

  // 1. Дожить до ближайшего прихода.
  const outlook = isKnown(input.needBaselineAmount)
    ? computeCycleOutlook({
      available: input.available.available,
      days: input.available.daysToHorizon,
      needBaseline: input.needBaselineAmount.value,
    })
    : null;
  const liquidity = computeLiquidity({
    today,
    accounts: input.accounts,
    ...(input.reserves ? { reserves: input.reserves } : {}),
    obligations: input.obligations,
    needBaseline: input.needBaselineAmount,
    wantBaseline: input.wantBaselineAmount,
  });
  // Ликвидность поднимает задачу независимо от месячного расчёта: месяц может
  // сходиться на бумаге, когда деньги лежат на вкладе, а на карте пусто.
  const criticalLiquidity = liquidity.known && liquidity.safety === 'critical';
  out.push({
    kind: 'survive_to_income',
    horizon: 'cycle',
    applicable: (outlook !== null && !outlook.covered) || criticalLiquidity,
    chosen: false,
    title: 'Дожить до прихода',
    amount: outlook ? outlook.shortfall : ZERO,
    why: outlook
      ? outlook.covered
        ? `Повседневные нужды до прихода хватает, но свободных денег на счетах мало`
        : `До ближайшего прихода ${outlook.days} дн., повседневные нужды нужно ${formatMoney(outlook.livingNeed)}, есть ${formatMoney(outlook.available)}`
      : 'Обычные траты неизвестны — сколько нужно до прихода, посчитать нельзя',
    action: outlook && !outlook.covered
      ? 'Сократить необязательное, перенести взнос в цель или отложить покупку в брони'
      : 'Проверить, какие деньги действительно доступны сейчас',
  });

  /*
    2. Отложить деньги на наступающие платежи.

    «Не хватает» значит «денег не хватит», а не «конверт не создан». Пока
    считалось второе, человек с 70 000 ₽ на счёте видел главной задачей
    мобильную связь за 650 ₽ — и переставал верить всему остальному. Конверт
    нужен там, где деньги иначе разойдутся; когда свободных денег хватает на
    всё сразу, тревожить незачем.
  */
  const unsecured = instances.filter((i) => !i.settled && i.remaining > ZERO);
  /*
    Сравниваем то, что сравнимо: платежи **до ближайшего прихода** против
    сегодняшних свободных денег. Сорок пять дней платежей против одного
    остатка дают недобор всегда — за месяц с лишним придёт зарплата, из
    которой половина этих платежей и платится.
  */
  const beforeIncome = unsecured.filter(
    (i) => !isAfter(i.scheduledDate, input.available.horizonDate),
  );
  const needed = add(...beforeIncome.map((i) => i.remaining));
  const shortOfMoney = needed > input.available.available;
  const nearestUnsecured = beforeIncome[0] ?? unsecured[0];
  out.push({
    kind: 'cover_obligations',
    horizon: 'cycle',
    // Считаем по платежам до прихода: если до него всё обеспечено, задачи
    // нет, каким бы ни был остаток на счёте.
    applicable: beforeIncome.length > 0 && shortOfMoney,
    chosen: false,
    title: 'Отложить деньги на платежи',
    amount: needed,
    why: nearestUnsecured
      ? `До ${humanDate(input.available.horizonDate)} нужно ${formatMoney(needed)}, `
        + `а свободно ${formatMoney(input.available.available)}. `
        + `Ближайший — ${nearestUnsecured.label} ${formatMoney(nearestUnsecured.amount)} ${humanDate(nearestUnsecured.scheduledDate)}`
      : 'На ближайшие платежи деньги отложены',
    action: nearestUnsecured
      ? `Отложить ${formatMoney(nearestUnsecured.remaining)} на «${nearestUnsecured.label}» или перенести что-то менее срочное`
      : 'Ничего делать не нужно',
    ...(nearestUnsecured
      ? {
        target: {
          kind: 'obligation' as const,
          id: nearestUnsecured.obligationId,
          dueDate: nearestUnsecured.scheduledDate,
          secured: nearestUnsecured.secured,
          missing: nearestUnsecured.remaining,
          execution: executionOf(nearestUnsecured, today),
        },
      }
      : {}),
  });

  // 3. Не попасть на проценты и просрочку.
  //
  //    Сюда же попадает обеспеченный, но невыполненный платёж: деньги готовы,
  //    а перевод всё ещё нужно сделать. Раньше это состояние пропадало.
  const overdue = instances.filter((i) => !i.settled && !isAfter(i.scheduledDate, today));
  const graceAtRisk = input.available.credit.filter((c: CreditState) => c.missing > ZERO);
  const dueSecured = overdue.filter((i) => i.remaining === ZERO);
  const first = overdue[0];
  const card = graceAtRisk[0];
  out.push({
    kind: 'avoid_interest_or_overdue',
    horizon: 'cycle',
    applicable: overdue.length > 0 || graceAtRisk.length > 0,
    chosen: false,
    title: card && !first ? 'Успеть внести, пока банк не берёт процентов' : 'Закрыть наступивший платёж',
    amount: card ? card.missing : first ? first.unpaid : ZERO,
    why: card
      ? `По карте «${card.name}» не хватает ${formatMoney(card.missing)} — внести до ${humanDate(card.paymentDate)}. `
        + 'Не успеть — и банк начнёт начислять проценты на весь долг'
      : first
        ? first.paid > ZERO
          ? `${first.label}: осталось ${formatMoney(first.unpaid)} из ${formatMoney(first.amount)} — оплачено ${formatMoney(first.paid)}. `
            + (dueSecured.includes(first) ? 'Деньги на доплату готовы' : `Срок — ${humanDate(first.scheduledDate)}`)
          : dueSecured.includes(first)
          ? `${first.label}: деньги готовы, но платёж от ${humanDate(first.scheduledDate)} не выполнен`
          : `${first.label} ${formatMoney(first.amount)} от ${humanDate(first.scheduledDate)} не оплачен`
        : 'Просроченных платежей нет',
    action: card
      ? `Внести ${formatMoney(card.missing)} на карту до ${humanDate(card.paymentDate)}`
      : first && first.paid > ZERO ? `Доплатить ${formatMoney(first.unpaid)} и подтвердить платёж` : 'Выполнить платёж и подтвердить его',
    ...(card
      ? {
        target: {
          kind: 'credit' as const,
          id: card.accountId,
          dueDate: card.paymentDate,
          secured: card.reserved,
          missing: card.missing,
          execution: card.settled ? ('paid' as const) : ('due' as const),
        },
      }
      : first
        ? {
          target: {
            kind: 'obligation' as const,
            id: first.obligationId,
            dueDate: first.scheduledDate,
            secured: first.secured,
            missing: first.remaining,
            execution: executionOf(first, today),
          },
        }
        : {}),
  });

  return out;
}

/** Месячные и будущие задачи — прежний `focusOptions`, приведённый к общему виду. */
function monthPriorities(input: PriorityInput): Priority[] {
  // Ноль в месяц на повседневные нужды — не ответ, а пустое поле: так
  // сохранялся пропущенный шаг онбординга. Считать по нему запас нельзя.
  const needKnown = isKnown(input.needBaselineAmount) && input.needBaselineAmount.value > ZERO;
  return focusOptions({ ...input, needKnown }).map((f: Focus): Priority => ({
    kind: f.kind,
    horizon: f.kind === 'goals' || f.kind === 'nothing_yet' ? 'future' : 'month',
    title: f.title,
    why: f.why,
    amount: f.amount,
    action: f.action,
    applicable: f.applicable,
    chosen: false,
  }));
}

/**
 * Главная задача и очередь за ней.
 *
 * Очередь собирается по правилу: в «дальше» попадают только задачи **с
 * датой** — платёж, грейс, срок. Подушка, цели и оптимизация излишка всегда в
 * «потом», независимо от их места в общем порядке: иначе экран превращается
 * в список дел, и ценность одной названной задачи исчезает.
 */
export function prioritize(input: PriorityInput): PriorityStack {
  const cycle = cyclePriorities(input);
  const month = monthPriorities(input);
  const all = [...cycle, ...month];

  const chosenKind = input.override;
  const applicable = all.filter((p) => p.applicable);

  // Ручной выбор человека уважается, но только среди применимых задач:
  // назначить главным то, чего нет, нельзя.
  const manual = chosenKind ? applicable.find((p) => p.kind === chosenKind) : undefined;
  const computed = applicable[0] ?? all[all.length - 1]!;
  const primary: Priority = manual
    ? { ...manual, chosen: true }
    : computed;

  // В очередь идут только задачи с датой и только применимые.
  const dated = applicable.filter(
    (p) => p !== primary && p.kind !== primary.kind
      && !UNDATED.includes(p.kind) && p.target?.dueDate,
  );
  const later = applicable.filter(
    (p) => p.kind !== primary.kind && !dated.includes(p),
  );

  return {
    primary,
    next: dated,
    later,
    all: all.map((p) => (p.kind === primary.kind ? primary : p)),
  };
}

/** Главная задача одна — для мест, где очередь не нужна. */
export const currentPriority = (input: PriorityInput): Priority => prioritize(input).primary;

/** Задача относится к ближнему горизонту: такие не откладываются выбором. */
export const isCyclePriority = (p: Priority): boolean => CYCLE_ORDER.includes(p.kind);

/**
 * Резерв или досрочное гашение — сравнение, а не константа.
 *
 * Универсального правила «сначала месячная подушка, затем дорогой долг» нет:
 * комментарий в коде продуктовой политикой не является. После защиты жизни и
 * обязательных платежей человеку показываются оба варианта с посчитанными
 * последствиями, и выбор остаётся за ним.
 */
export interface TradeoffOption {
  kind: 'build_buffer' | 'repay_debt';
  targetId: string;
  amount: Money;
  effects: {
    /** На сколько дней вырастет запас хода. */
    runwayDaysGained: number | null;
    /** Сколько процентов в год экономит гашение. */
    debtSavedPerYear: Money;
    /** Что останется нерешённым после этого шага. */
    leavesOpen: string;
  };
}

export function compareReserveVsRepay(input: {
  amount: Money;
  buffer?: Goal | undefined;
  loans: readonly Loan[];
  /** Обычный дневной расход — из него считается прирост запаса хода. */
  usualPerDay: Money;
}): TradeoffOption[] {
  const out: TradeoffOption[] = [];
  const expensive = [...input.loans]
    .filter((l) => l.principal > ZERO && annualCost(l) > ZERO)
    .sort((a, b) => b.ratePct - a.ratePct)[0];

  if (input.buffer) {
    const gap = clampPos(sub(input.buffer.targetAmount, input.buffer.currentAmount));
    const toBuffer = gap < input.amount ? gap : input.amount;
    out.push({
      kind: 'build_buffer',
      targetId: input.buffer.id,
      amount: toBuffer,
      effects: {
        runwayDaysGained: input.usualPerDay > ZERO ? Math.floor(toBuffer / input.usualPerDay) : null,
        debtSavedPerYear: ZERO,
        leavesOpen: expensive
          ? `Долг «${expensive.name}» продолжит стоить ${formatMoney(annualCost(expensive))} в год`
          : 'Ничего срочного не остаётся',
      },
    });
  }

  if (expensive) {
    const toDebt = expensive.principal < input.amount ? expensive.principal : input.amount;
    out.push({
      kind: 'repay_debt',
      targetId: expensive.id,
      amount: toDebt,
      effects: {
        runwayDaysGained: 0,
        debtSavedPerYear: savedPerYear(expensive, toDebt),
        leavesOpen: input.buffer
          ? `Подушка останется на ${formatMoney(input.buffer.currentAmount)} — неожиданность снова уйдёт в кредит`
          : 'Подушки нет: следующая неожиданность снова уйдёт в долг',
      },
    });
  }

  return out;
}

/** Решение человека. Хранится и участвует в расчёте, а не только в надписи. */
export interface TradeoffChoice {
  kind: TradeoffOption['kind'];
  targetId: string;
  amount: Money;
  decidedAt: string;
}
